import React from 'react';

import { useIsRestoring, type UseQueryResult } from '@tanstack/react-query';

type BaseQuery = UseQueryResult<unknown, unknown>;

type DestructuredQueryResults<T extends readonly BaseQuery[]> = {
	[K in keyof T]: T[K] extends UseQueryResult<infer TData, unknown>
		? TData
		: never
};

const isDataEmpty = (data: unknown): boolean => {
	if (data === null || data === undefined) return true;
	if (Array.isArray(data) && data.length === 0) return true;
	if (
		typeof data === 'object' &&
		!Array.isArray(data) &&
		Object.keys(data).length === 0
	) {
		return true;
	}
	return false;
};

interface WithStateQueryBaseProps {
	loading?: React.ReactNode;
	error?: React.ReactNode;
	empty?: React.ReactNode;
	withOtherLoading?: boolean[];
}

type WithStateQueryProps<TState extends BaseQuery | readonly BaseQuery[]> =
	WithStateQueryBaseProps & {
		state: TState,
		children: TState extends readonly BaseQuery[]
			? (data: DestructuredQueryResults<TState>) => React.ReactNode
			: TState extends UseQueryResult<infer TData, unknown>
				? React.ReactNode | ((data: TData) => React.ReactNode)
				: never
	};

export function WithStateQuery<
	const TState extends BaseQuery | readonly BaseQuery[]
>(props: WithStateQueryProps<TState>): React.ReactElement | null {
	const { state, children, empty, error, loading, withOtherLoading } = props;
	const isRestoring = useIsRestoring();

	const queries = Array.isArray(state)
		? (state as BaseQuery[])
		: [state as BaseQuery];

	const isLoading =
		isRestoring ||
		withOtherLoading?.some(Boolean) ||
		queries.some((q) => q.isLoading);

	const isError = queries.some((q) => q.isError);
	const allSuccessful = queries.every((q) => q.isSuccess);
	const hasEmptyData = queries.some((q) => isDataEmpty(q.data));

	if (isLoading) return (loading as React.ReactElement) ?? null;
	if (isError) return (error as React.ReactElement) ?? null;
	if (allSuccessful && hasEmptyData)
		return (empty as React.ReactElement) ?? null;
	if (!allSuccessful) return (loading as React.ReactElement) ?? null;

	if (Array.isArray(state)) {
		const arrayState = state as readonly BaseQuery[];

		const data = arrayState.map((q) => q.data) as DestructuredQueryResults<
			typeof arrayState
		>;

		const arrayChildren = children as (d: typeof data) => React.ReactNode;

		return (arrayChildren(data) as React.ReactElement) ?? null;
	}

	const singleState = state as BaseQuery;

	if (typeof children === 'function') {
		const fnChildren = children as (data: unknown) => React.ReactNode;
		return (fnChildren(singleState.data) as React.ReactElement) ?? null;
	}

	return (children as React.ReactElement) ?? null;
}
